import { digger } from '@cbre/digger';
import {
  areaUomAbbrev,
  concatValuesBy,
  defaultTxt,
  EMPTY_STRING,
  formatNumber,
  getPropertyTypeLabel,
} from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import { DisplayFnArgumentsInfo, LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { DOLLAR_SIGN, formatCompanyContactByRole, getUOMValue, PERCENT_SIGN, RoleDescriptions } from '../us.common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  321: {
    // Building Name
    displayFn: displayFnWrapper,
  },
  322: {
    // 322 Address
    displayFn: (record: ReportRecord, _, street, subMarket, district) => {
      const values = [subMarket, district].map(arg => {
        const mappedValue = mapAndFormatDynamicValue(record, arg) as string;
        const value = mappedValue == null ? EMPTY_STRING : mappedValue;
        return value === defaultTxt ? EMPTY_STRING : value;
      });
      const topVal = mapAndFormatDynamicValue(record, street);
      const bottomVal = values.every(value => value == EMPTY_STRING)
        ? defaultTxt
        : values.some(value => value == EMPTY_STRING)
          ? values.join('')
          : values.join('/');
      return concatValuesBy(' \n', topVal, bottomVal);
    },
  },
  323: {
    // 323 Size (SF)
    displayFn: displayFnWrapper,
  },
  324: {
    // 324 Price Per SF Cap Rate
    displayFn: (record: ReportRecord, _, pricePerSF, capRate) => {
      const [price] = mapAndFormatDynamicValue(record, pricePerSF, [], false);
      const [rate] = mapAndFormatDynamicValue(record, capRate);
      const values = concatValuesBy(
        '\n ',
        price == null || price === defaultTxt ? defaultTxt : DOLLAR_SIGN + price,
        rate == null || rate === defaultTxt ? defaultTxt : rate + PERCENT_SIGN
      );
      return values;
    },
  },
  325: {
    // 325 Sale Price
    displayFn: (record: ReportRecord, _, salePrice) => {
      const [price] = mapAndFormatDynamicValue(record, salePrice, [], false);
      return price == null || price === defaultTxt ? defaultTxt : DOLLAR_SIGN + price;
    },
  },
  326: {
    // 326 Property Type Class
    displayFn: (record: ReportRecord, _, propertyType, propertyClass) => {
      const propertyTypeValue = mapAndFormatDynamicValue(record, propertyType);
      const propertyTypeLabel = getPropertyTypeLabel(propertyTypeValue as string);
      const values = concatValuesBy(' \n', propertyTypeLabel, mapAndFormatDynamicValue(record, propertyClass));
      return values;
    },
  },
  327: {
    // 327 Buyer
    displayFn: (record: ReportRecord, _, arg: DisplayFnArgumentsInfo) => {
      const result = formatCompanyContactByRole(
        record,
        arg,
        [RoleDescriptions.TrueBuyer, RoleDescriptions.LegalBuyer, RoleDescriptions.BuyerRep],
        '|',
        'company_contact_role_addresses'
      );
      return result === EMPTY_STRING ? '-' : result;
    },
  },
  328: {
    // 328 Seller
    displayFn: (record: ReportRecord, _, arg: DisplayFnArgumentsInfo) => {
      const result = formatCompanyContactByRole(
        record,
        arg,
        [RoleDescriptions.TrueSeller, RoleDescriptions.LegalSeller, RoleDescriptions.SellerRep],
        '|',
        'company_contact_role_addresses'
      );
      return result === EMPTY_STRING ? '-' : result;
    },
  },
  330: {
    // 330 Total Size (SF)
    displayFn: (record: ReportRecord[], _, space) => {
      let totalSize = 0;
      let uom = null;
      record.forEach(rec => {
        const size = digger(rec.data, space.field);
        const uomValue = space.uomArgument ? digger(rec.data, space.uomArgument.field) : null;
        uom = uomValue ? getUOMValue(uom, uomValue) : null;
        if (size) {
          totalSize += parseInt(size);
        }
      });
      if (space.uomArgument) {
        return space.uomArgument.lovValues.pipe(
          map((currencyLOV: LovValues[]) => {
            if (uom && currencyLOV) {
              const lovValue = currencyLOV.find(a => a.key === uom);
              if (lovValue) {
                uom = areaUomAbbrev(lovValue.value);
              }
            }
            return formatNumber(totalSize) + ' ' + (uom || '');
          })
        );
      } else {
        return formatNumber(totalSize) + ' SF';
      }
    },
  },
};
